import { supabase } from '../lib/supabase';

export const settingsService = {
  // System Settings
  async getSystemSettings() {
    const { data, error } = await supabase?.from('system_settings')?.select('*')?.order('setting_key');
    
    if (error) throw error;
    
    // Convert to camelCase
    return data?.map(setting => ({
      id: setting?.id,
      settingKey: setting?.setting_key,
      settingValue: setting?.setting_value,
      description: setting?.description,
      createdAt: setting?.created_at,
      updatedAt: setting?.updated_at,
      updatedBy: setting?.updated_by
    }));
  },

  async getSettingByKey(key) {
    try {
      const { data, error } = await supabase?.from('system_settings')?.select('*')?.eq('setting_key', key)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows
      if (error?.code === 'PGRST116' || !data) return null;

      return {
        id: data?.id,
        settingKey: data?.setting_key,
        settingValue: data?.setting_value,
        description: data?.description,
        updatedAt: data?.updated_at,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error fetching setting by key:', error);
      throw error;
    }
  },

  async updateSystemSetting(id, settingValue) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('system_settings')?.update({
        setting_value: settingValue,
        updated_by: user?.id
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      settingKey: data?.setting_key,
      settingValue: data?.setting_value,
      description: data?.description,
      updatedAt: data?.updated_at,
      updatedBy: data?.updated_by
    };
  },

  // Signup Passcode Management
  async getSignupPasscode() {
    const { data, error } = await supabase?.from('signup_passcodes')?.select('*')?.single();
    
    if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows
    if (!data) return null;
    
    return {
      id: data?.id,
      passcode: data?.passcode,
      isRequired: data?.is_required,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      createdBy: data?.created_by
    };
  },

  async updateSignupPasscode(passcode, isRequired) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if passcode exists
    const { data: existing } = await supabase?.from('signup_passcodes')?.select('id')?.single();

    if (existing) {
      // Update existing
      const { data, error } = await supabase?.from('signup_passcodes')?.update({
          passcode: passcode,
          is_required: isRequired
        })?.eq('id', existing?.id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        passcode: data?.passcode,
        isRequired: data?.is_required,
        updatedAt: data?.updated_at
      };
    } else {
      // Create new
      const { data, error } = await supabase?.from('signup_passcodes')?.insert({
          passcode: passcode,
          is_required: isRequired,
          created_by: user?.id
        })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        passcode: data?.passcode,
        isRequired: data?.is_required,
        createdAt: data?.created_at
      };
    }
  },

  async validatePasscode(inputPasscode) {
    const { data, error } = await supabase?.from('signup_passcodes')?.select('passcode, is_required')?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    if (!data) return { valid: true, required: false }; // No passcode set
    
    return {
      valid: !data?.is_required || data?.passcode === inputPasscode,
      required: data?.is_required
    };
  },

  // Coupon Code Management
  async getCouponCodes() {
    const { data, error } = await supabase?.from('coupon_codes')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(coupon => ({
      id: coupon?.id,
      code: coupon?.code,
      discountPercentage: coupon?.discount_percentage,
      usageLimit: coupon?.usage_limit,
      usageCount: coupon?.usage_count,
      expirationDate: coupon?.expiration_date,
      isActive: coupon?.is_active,
      createdAt: coupon?.created_at,
      updatedAt: coupon?.updated_at,
      createdBy: coupon?.created_by,
      updatedBy: coupon?.updated_by
    }));
  },

  async createCouponCode(couponData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('coupon_codes')?.insert({
        code: couponData?.code?.toUpperCase(),
        discount_percentage: couponData?.discountPercentage,
        usage_limit: couponData?.usageLimit || null,
        expiration_date: couponData?.expirationDate || null,
        is_active: true,
        created_by: user?.id,
        updated_by: user?.id
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      code: data?.code,
      discountPercentage: data?.discount_percentage,
      usageLimit: data?.usage_limit,
      usageCount: data?.usage_count,
      expirationDate: data?.expiration_date,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async updateCouponCode(id, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      updated_by: user?.id
    };

    if (updates?.discountPercentage !== undefined) {
      updateData.discount_percentage = updates?.discountPercentage;
    }
    if (updates?.usageLimit !== undefined) {
      updateData.usage_limit = updates?.usageLimit;
    }
    if (updates?.expirationDate !== undefined) {
      updateData.expiration_date = updates?.expirationDate;
    }
    if (updates?.isActive !== undefined) {
      updateData.is_active = updates?.isActive;
    }

    const { data, error } = await supabase?.from('coupon_codes')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      code: data?.code,
      discountPercentage: data?.discount_percentage,
      usageLimit: data?.usage_limit,
      usageCount: data?.usage_count,
      expirationDate: data?.expiration_date,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  async deleteCouponCode(id) {
    const { error } = await supabase?.from('coupon_codes')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  async validateCouponCode(code) {
    const { data, error } = await supabase?.from('coupon_codes')?.select('*')?.eq('code', code?.toUpperCase())?.eq('is_active', true)?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    if (!data) return { valid: false, message: 'Invalid coupon code' };

    // Check expiration
    if (data?.expiration_date && new Date(data.expiration_date) < new Date()) {
      return { valid: false, message: 'Coupon code has expired' };
    }

    // Check usage limit
    if (data?.usage_limit && data?.usage_count >= data?.usage_limit) {
      return { valid: false, message: 'Coupon code usage limit reached' };
    }

    return {
      valid: true,
      discountPercentage: data?.discount_percentage,
      code: data?.code
    };
  },

  async incrementCouponUsage(code) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.rpc('increment_coupon_usage', {
      coupon_code: code?.toUpperCase()
    });
    
    if (error) {
      // If RPC doesn't exist, use direct update
      const { data: coupon } = await supabase?.from('coupon_codes')?.select('usage_count')?.eq('code', code?.toUpperCase())?.single();
      
      if (coupon) {
        await supabase?.from('coupon_codes')?.update({
            usage_count: (coupon?.usage_count || 0) + 1,
            updated_by: user?.id
          })?.eq('code', code?.toUpperCase());
      }
    }
  },

  // Site Favicon Management
  async getSiteFavicon() {
    try {
      const setting = await this.getSettingByKey('site_favicon');
      return setting?.settingValue ? JSON.parse(setting?.settingValue) : null;
    } catch (error) {
      console.error('Error fetching site favicon:', error);
      return null;
    }
  },

  async uploadFavicon(file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Validate file type
    const allowedTypes = ['image/svg+xml', 'image/x-icon', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      throw new Error('Invalid file type. Only SVG, ICO, and PNG files are allowed.');
    }

    // Validate file size (512KB max)
    if (file?.size > 524288) {
      throw new Error('File size exceeds 512KB limit.');
    }

    try {
      // Delete old favicon if exists
      const oldFavicon = await this.getSiteFavicon();
      if (oldFavicon?.path) {
        await supabase?.storage
          ?.from('site-favicon')
          ?.remove([oldFavicon?.path]);
      }

      // Upload new favicon with timestamp to avoid caching issues
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `favicon-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('site-favicon')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('site-favicon')
        ?.getPublicUrl(filePath);

      // Save to system settings
      const faviconData = {
        url: urlData?.publicUrl,
        path: filePath,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        uploadedAt: new Date()?.toISOString()
      };

      // Check if setting exists
      const existingSetting = await this.getSettingByKey('site_favicon');

      if (existingSetting) {
        // Update existing setting
        await this.updateSystemSetting(existingSetting?.id, JSON.stringify(faviconData));
      } else {
        // Create new setting
        const { error: insertError } = await supabase?.from('system_settings')?.insert({
            setting_key: 'site_favicon',
            setting_value: JSON.stringify(faviconData),
            description: 'Site favicon SVG file',
            updated_by: user?.id
          });

        if (insertError) throw insertError;
      }

      return faviconData;
    } catch (error) {
      console.error('Error uploading favicon:', error);
      throw error;
    }
  },

  async deleteFavicon() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const favicon = await this.getSiteFavicon();
      if (!favicon) {
        throw new Error('No favicon found to delete');
      }

      // Delete from storage
      const { error: storageError } = await supabase?.storage
        ?.from('site-favicon')
        ?.remove([favicon?.path]);

      if (storageError) throw storageError;

      // Delete from system settings
      const existingSetting = await this.getSettingByKey('site_favicon');
      if (existingSetting) {
        const { error: deleteError } = await supabase?.from('system_settings')?.delete()?.eq('id', existingSetting?.id);

        if (deleteError) throw deleteError;
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting favicon:', error);
      throw error;
    }
  }
};